/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import * as SDK from 'azure-devops-extension-sdk';
import { CoreRestClient } from 'azure-devops-extension-api/Core';
import { StepByStep } from './step-by-step';
import { Instructions } from './instructions';
import { Authorization } from './authorization';
import { Finnish } from './finnish';
import { checkProjectAuthorizationService } from 'src/services/appgami-api/check-project-authorization';
import AuhtorizationSkeleton from './skeleton';
import { CommonServiceIds } from 'azure-devops-extension-api/Common';

interface AuthorizationStepContextType {
  step: number;
  setStep: (step: number) => void;
  onClose?: () => void;
  adminUrl: string;
  organization: {
    name: string;
    id: string;
  };
  authorization: {
    service: boolean;
    user: boolean;
  },
  setAuthorization: (authorization: {
    service: boolean;
    user: boolean;
  }) => void;
}

const StepContent = ({ step }: { step: number }) => {
	switch (step) {
	case 1:
		return <Instructions />;
	case 2:
		return <Authorization />;
	case 3:
		return <Finnish />;
	default:
		return null;
	}
};

const AuthorizationStepContext = createContext({} as AuthorizationStepContextType);

export const useAuthorizationStep = () => {
	return useContext(AuthorizationStepContext);
};

interface Props {
  organizationName: string;
  organizationId: string;
  onClose?: () => void;
}

export function AuthorizationSteps(props: Props) {
	const extensionCtx = SDK.getExtensionContext();


	const [step, setStep] = useState(1);
	const [state, setState] = useState({
		loading: true,
		adminUrl: '',
		authorization: {
			service: false,
			user: false
		}
	});

	const setAuthorization = useCallback((authorization: {
    service: boolean;
    user: boolean;
  }) => {
		setState(oldState => ({
			...oldState,
			authorization
		}));
	}, []);

	const changeState = useCallback((newState: Partial<typeof state>) => {
		setState(oldState => ({
			...oldState,
			...newState
		}));
	}, []);

	const mountAdminUrl = useCallback(async () => {
		await SDK.ready();
		const locationService: any = await SDK.getService(
			CommonServiceIds.LocationService
		);

		const hostBaseUrl = await locationService.getResourceAreaLocation(
			CoreRestClient.RESOURCE_AREA_ID
		);

		changeState({ adminUrl: `${hostBaseUrl}_settings/${extensionCtx.id}.appgami-automation.admin-hub` });
	}, []);

	const checkProjectAuthorization = useCallback(async () => {
		try {
			const result = await checkProjectAuthorizationService({
				organizationName: props.organizationName
			});
	
			if (result.type === 'success') {
				changeState({
					authorization: {
						service: result.data.servicePrincipalAuthorization,
						user: result.data.userAuthorization
					}
				});
	
				if (result.data.servicePrincipalAuthorization) {
					setStep(3);
				}

				if (result.data.userAuthorization) {
					setStep(3);
				}
			}

		} finally {
			changeState({ loading: false });
		}
	}, []);

	useEffect(() => {
		const asyncEffect = async () => {
			await mountAdminUrl();
			await checkProjectAuthorization();
		};
		asyncEffect();
	}, []);

	if (state.loading) {
		return <AuhtorizationSkeleton />;
	}

	return (
		<AuthorizationStepContext.Provider
			value={{
				step,
				setStep,
				onClose: props.onClose,
				authorization: state.authorization,
				adminUrl: state.adminUrl,
				setAuthorization,
				organization: {
					name: props.organizationName,
					id: props.organizationId,
				},
			}}
		>
			<StepByStep
				step={step}
			>
				<StepContent step={step} />
			</StepByStep>
		</AuthorizationStepContext.Provider>
	);
}
